import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Skeleton, Modal, Spin, Input, message } from "antd";
import { Tooltip } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import TableChartIcon from "@mui/icons-material/TableChart";
import styles from "./ResultsView.module.scss";
import barChartStyles from "../StackedBarChart/StackedBarChart.module.scss";
import KeywordTopFacultySection from "../KeywordTopFacultySection/KeywordTopFacultySection";
import StackedBarChart from "../StackedBarChart/StackedBarChart";
import GroupedMetricBars from "../GroupedMetricBars/GroupedMetricBars";
import PieChart from "../PieChart/PieChart";
import BreakdownTable from "../BreakdownTable/BreakdownTable";
import BenchmarkHeatmap from "../BenchmarkHeatmap/BenchmarkHeatmap";
import BenchmarkScatterChart from "../BenchmarkScatterChart/BenchmarkScatterChart";
import AdminBenchmarkPanel from "../AdminBenchmarkPanel/AdminBenchmarkPanel";
import DashboardContextSection from "../DashboardContextSection/DashboardContextSection";
import ResearchTopicsRankingView from "../ResearchTopicsRankingView/ResearchTopicsRankingView";
import CitationByYearChart from "../CitationByYearChart/CitationByYearChart";
import BenchmarkEntityPicker from "../BenchmarkEntityPicker/BenchmarkEntityPicker";
import RelatedConceptsPanel from "../RelatedConceptsPanel/RelatedConceptsPanel";
import {
  getKeywordStatistics,
  getKeywordDashboardContext,
  getBenchmarkKeywordCloud,
  getResearcherBenchmarkStatistics,
  getUnitBenchmarkStatistics,
  getVenueBenchmarkStatistics,
} from "Explore/Services/KeywordBenchmarking";
import { getSuggestSearchInstitutions } from "Explore/Services/Institution";
import { getSuggestSearchAuthors } from "Explore/Services/Author";
import { getSuggestSearchUnits } from "Explore/Services/Unit";
import { getSuggestSearchVenues } from "Explore/Services/Venue";
import { getSuggestKeywords } from "Explore/Services/Common";
import {
  aggregateBenchmarkByInstitution,
  BENCHMARK_BAR_STATS,
  BENCHMARK_CORE_STATS,
  BENCHMARK_INSTITUTION_COLORS,
  BENCHMARK_METRIC_SECTIONS,
  BENCHMARK_PIE_STATS,
  BENCHMARK_SUB_COLS,
  filterMetricsByMode,
  formatBenchmarkStatValue,
  getPaletteColors,
} from "../../benchmarkMetricConfig";
import {
  BENCHMARK_MODES,
  ENTITY_ID_KEY_BY_MODE,
  ENTITY_LABEL_BY_MODE,
  collectMetricByYearSeries,
  normalizeRawDataForCharts,
  objectTypeFromBenchmarkMode,
} from "../../benchmarkEntityAdapters";
import { getEntityPickerInitialStateFromCompareQueue } from "Explore/Utils/compareQueueStorage";
import {
  DEFAULT_ADMIN_CONFIG,
  mergeAdminConfig,
} from "../../benchmarkAdminConfig";
import {
  getCurrentBenchmarkConfig,
  saveBenchmarkConfig,
  getBenchmarkConfigVersions,
  getBenchmarkConfigVersion,
  rollbackBenchmarkConfig,
} from "Explore/Services/BenchmarkConfiguration";
import {
  captureReportItems,
  captureReportElement,
  captureReactAtSize,
  captureFacultyCardPng,
  exportBenchmarkPairBreakdownExcel,
  exportReportPdf,
} from "./exportUtils";
import {
  REPORT_TEMPLATE,
  getReportAnchors,
  exportBenchmarkReport,
  PPTX_CHART_CODE_TO_REPORT_ID,
  PPTX_TABLE_CODE_TO_REPORT_ID,
  PPTX_TOP_FACULTY_REPORT_ID,
  PPTX_METRIC_SECTIONS,
  pptxSectionImageCode,
  pptxSectionNameCode,
  pptxTopFacultyCode,
  DEFAULT_PPTX_ANCHORS,
} from "Explore/Services/BenchmarkReport";

const capitalize = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

const modeSvg = (children) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);

const MODE_ICONS = {
  [BENCHMARK_MODES.RESEARCH_TOPICS_OVERVIEW]: modeSvg(
    <>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="14" y2="17" />
    </>
  ),
  [BENCHMARK_MODES.INSTITUTION]: modeSvg(
    <>
      <path d="M3 10l9-6 9 6" />
      <path d="M4 10v10M20 10v10M9 10v10M15 10v10" />
      <path d="M2 20h20" />
    </>
  ),
  [BENCHMARK_MODES.RESEARCHER]: modeSvg(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </>
  ),
  [BENCHMARK_MODES.UNIT]: modeSvg(
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  [BENCHMARK_MODES.VENUE]: modeSvg(
    <>
      <path d="M5 4a2 2 0 0 1 2-2h11v18H7a2 2 0 0 0-2 2z" />
      <line x1="5" y1="18" x2="18" y2="18" />
    </>
  ),
};

const MODE_OPTIONS = [
  {
    value: BENCHMARK_MODES.RESEARCH_TOPICS_OVERVIEW,
    label: "Research topics ranking",
  },
  { value: BENCHMARK_MODES.INSTITUTION, label: "Benchmark by institution" },
  { value: BENCHMARK_MODES.RESEARCHER, label: "Benchmark by researcher" },
  { value: BENCHMARK_MODES.UNIT, label: "Benchmark by unit" },
  { value: BENCHMARK_MODES.VENUE, label: "Benchmark by venue" },
];

// Report items rendered as a table only (chart image skipped) — the on-screen
// chart shows one topic tab at a time, so the table is the complete view.
const REPORT_NO_CAPTURE_IDS = new Set(["top-faculty"]);

// Report ids that can be exported to PPTX (chart + table codes, the per-keyword
// top-faculty cards, and the metric sections → text placeholders).
const PPTX_REPORT_IDS = new Set([
  ...Object.values(PPTX_CHART_CODE_TO_REPORT_ID),
  ...Object.values(PPTX_TABLE_CODE_TO_REPORT_ID),
  PPTX_TOP_FACULTY_REPORT_ID,
  ...Object.keys(PPTX_METRIC_SECTIONS).map((id) => `section-${id}`),
]);

// Segmented time-window control (maps to the API `time_filter` values).
const TIME_FILTER_OPTIONS = [
  { value: "last_5_years", label: "Last 5y" },
  { value: "last_10_years", label: "Last 10y" },
  { value: "all", label: "All years" },
];

const SCALING_TABLE_TH = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  color: "#334155",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
};

const SCALING_TABLE_TD = {
  padding: "9px 12px",
  fontSize: 13,
  color: "#475569",
  borderBottom: "1px solid #eef2f6",
  whiteSpace: "nowrap",
};

const ENTITY_PICKER_CONFIG = {
  [BENCHMARK_MODES.INSTITUTION]: {
    label: "Pair institutions",
    placeholder: "Search institutions...",
    emptyHint: "Type at least 2 characters to search.",
    functionSearch: getSuggestSearchInstitutions,
    valueField: "id",
  },
  [BENCHMARK_MODES.RESEARCHER]: {
    label: "Pair researchers",
    placeholder: "Search researchers...",
    emptyHint: "Type at least 2 characters to search.",
    functionSearch: getSuggestSearchAuthors,
    valueField: "id",
  },
  [BENCHMARK_MODES.UNIT]: {
    label: "Pair units",
    placeholder: "Search units...",
    emptyHint: "Type at least 2 characters to search.",
    functionSearch: getSuggestSearchUnits,
    valueField: "id",
  },
  [BENCHMARK_MODES.VENUE]: {
    label: "Pair venues",
    placeholder: "Search venues...",
    emptyHint: "Type at least 2 characters to search.",
    functionSearch: getSuggestSearchVenues,
    valueField: "id",
  },
};

const LazyMountSection = ({ children, minHeight = 360 }) => {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setMounted(true);
          io.disconnect();
        }
      },
      { rootMargin: "240px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mounted]);

  return (
    <div ref={ref}>
      {mounted ? children : <div style={{ minHeight }} aria-hidden />}
    </div>
  );
};

const ReportableChart = ({ id, title, selected, onToggle, children }) => (
  <div className={styles["reportable"]} data-report-id={id}>
    <button
      type="button"
      data-report-ignore="true"
      className={`${styles["report-toggle"]} ${
        selected ? styles["report-toggle--on"] : ""
      }`}
      onClick={() => onToggle(id, title)}
      title={selected ? "Remove from report" : "Add this to the PDF report"}
    >
      {selected ? "✓ Added to report" : "+ Add to report"}
    </button>
    {children}
  </div>
);

const EXPORT_TABLE_INSET = 12; // card padding on every side (CSS px)

// `width`/`height` are the anchor box in the SAME CSS px the capture host uses
// (anchor width_px/height_px ÷ the 2× capture scale). Font size and cell
// padding are DERIVED from the per-row height available inside the box, so a
// dense table shrinks to fit instead of overflowing the fixed anchor box, and
// a sparse one gets comfortable (capped) padding — nothing is hard-coded.
const ExportTable = ({ columns = [], rows = [], width = 0, height = 0 }) => {
  const lastCol = columns.length - 1;
  const totalRows = Math.max(1, rows.length + 1); // header + body rows
  const innerH = Math.max(0, height - EXPORT_TABLE_INSET * 2);
  const innerW = Math.max(0, width - EXPORT_TABLE_INSET * 2);
  const rowH = innerH / totalRows;

  const fontSize = 12;
  const vPad = Math.max(2, Math.min(16, Math.round((rowH - fontSize * 1.5) / 2)));
  const hPad = Math.max(8, Math.min(20, Math.round(innerW * 0.012)));
  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,0.06)",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: "#fff",
        borderRadius: "14px",
        boxSizing: "border-box",
        padding: `${EXPORT_TABLE_INSET}px`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <table
        style={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          borderCollapse: "collapse",
          color: "#334155",
        }}
      >
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th
                key={i}
                style={{
                  background: "#f7f8fa",
                  padding: `${vPad}px ${hPad}px`,
                  fontSize,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: i === 0 ? "0.8px" : "0.6px",
                  color: i === 0 ? "#666" : "#0f3460",
                  textAlign: i === 0 ? "left" : "center",
                  borderBottom: "2px solid rgba(0,0,0,0.08)",
                  borderRight:
                    i === lastCol ? "none" : "1px solid rgba(0,0,0,0.1)",
                  wordBreak: "break-word",
                  verticalAlign: "middle",
                }}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    background: "#f8fafc",
                    padding: `${vPad}px ${hPad}px`,
                    fontSize,
                    fontWeight: ci === 0 ? 600 : 500,
                    color: ci === 0 ? "#0f3460" : "#333",
                    textAlign: ci === 0 ? "left" : "center",
                    fontVariantNumeric: "tabular-nums",
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                    borderRight:
                      ci === lastCol ? "none" : "1px solid rgba(0,0,0,0.1)",
                    whiteSpace: ci === 0 ? "nowrap" : "normal",
                    verticalAlign: "middle",
                  }}
                >
                  {cell == null || cell === "" ? "—" : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ResultsView = ({ initialKeywords, admin = false }) => {
  const [selectedKeywords, setSelectedKeywords] = useState(initialKeywords);
  const [benchmarkMode, setBenchmarkMode] = useState(
    BENCHMARK_MODES.RESEARCH_TOPICS_OVERVIEW
  );
  // Time window applied to every benchmark/overview request (global across modes).
  const [timeFilter, setTimeFilter] = useState("all");

  const isLoggedIn = useMemo(
    () =>
      typeof window !== "undefined" &&
      !!window.localStorage.getItem(
        `${process.env.REACT_APP_PREFIX}_access_token`
      ),
    []
  );
  const [adminConfig, setAdminConfig] = useState(DEFAULT_ADMIN_CONFIG);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const applyAdminConfig = true;

  const [configVersion, setConfigVersion] = useState(null);
  const [configLabel, setConfigLabel] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [savingVersion, setSavingVersion] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [versions, setVersions] = useState([]);
  const [versionsTotal, setVersionsTotal] = useState(0);
  const [versionsPage, setVersionsPage] = useState(0);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [reverting, setReverting] = useState(false);
  const VERSIONS_LIMIT = 20;

  // Load the user's current saved configuration on mount (logged-in only).
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    getCurrentBenchmarkConfig()
      .then((data) => {
        if (cancelled || !data) return;
        setAdminConfig(mergeAdminConfig(data.config));
        setConfigVersion(data.version ?? null);
        setConfigLabel(data.label ?? null);
      })
      .catch(() => {
        /* first-run / offline: keep defaults */
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(96);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => setHeaderHeight(el.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Below this width the header/rail unpin to normal flow (see SCSS media query),
  // so we drop the fixed-layout inline offsets.
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 960px)");
    const on = () => setIsNarrow(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  const railWidth = railCollapsed ? 68 : 272;
  // In admin view the page sits inside a layout with a 250px left sidebar. The
  // fixed header/rail escape normal flow, so they must be inset by that width.
  const contentLeftInset = admin && !isNarrow ? 250 : 0;

  const handleAdminConfigChange = useCallback((next) => {
    setAdminConfig(next);
    setPreviewVersion(null);
  }, []);

  const loadVersions = useCallback(async (page = 0) => {
    setVersionsLoading(true);
    try {
      const res = await getBenchmarkConfigVersions({
        page,
        limit: VERSIONS_LIMIT,
      });
      setVersions(res.data);
      setVersionsTotal(res.total);
      setVersionsPage(res.page);
    } catch (e) {
      message.error("Could not load version history.");
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  const openSaveModal = useCallback(() => {
    setSaveLabel("");
    setSaveModalOpen(true);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    setSavingVersion(true);
    try {
      const saved = await saveBenchmarkConfig({
        config: adminConfig,
        label: saveLabel.trim() || null,
      });
      if (saved) {
        setConfigVersion(saved.version ?? null);
        setConfigLabel(saved.label ?? null);
        setPreviewVersion(null);
        message.success(`Saved as version ${saved.version}.`);
      }
      setSaveModalOpen(false);
    } catch (e) {
      message.error("Could not save configuration. Please try again.");
    } finally {
      setSavingVersion(false);
    }
  }, [adminConfig, saveLabel]);

  const handlePreviewVersion = useCallback(async (version) => {
    try {
      const data = await getBenchmarkConfigVersion(version);
      if (data) {
        setAdminConfig(mergeAdminConfig(data.config));
        setPreviewVersion(version);
        message.info(
          `Previewing version ${version}. Save it or roll back to keep it.`
        );
      }
    } catch (e) {
      message.error("Could not load that version.");
    }
  }, []);

  const handleRevertVersion = useCallback(
    async (version) => {
      setReverting(true);
      try {
        await rollbackBenchmarkConfig({ target_version: version });
        const current = await getCurrentBenchmarkConfig();
        if (current) {
          setAdminConfig(mergeAdminConfig(current.config));
          setConfigVersion(current.version ?? null);
          setConfigLabel(current.label ?? null);
        }
        setPreviewVersion(null);
        await loadVersions(0);
        message.success(`Rolled back to version ${version}.`);
      } catch (e) {
        message.error("Could not roll back. Please try again.");
      } finally {
        setReverting(false);
      }
    },
    [loadVersions]
  );

  // Per-mode selected entity ids + display label maps (seeded from compare queue on mount).
  const [entityIdsByMode, setEntityIdsByMode] = useState(() => {
    const s = getEntityPickerInitialStateFromCompareQueue();
    return s.entityIdsByMode;
  });
  const [entityLabelMaps, setEntityLabelMaps] = useState(() => {
    const s = getEntityPickerInitialStateFromCompareQueue();
    return s.entityLabelMaps;
  });

  // Benchmark statistics state (one slot per mode keyed by entity).
  const [rawData, setRawData] = useState([]);
  const [activeDataMode, setActiveDataMode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const statsRequestRef = useRef(0);

  // Dashboard context state (Research Topics Overview only).
  const [dashboardData, setDashboardData] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const dashboardRequestRef = useRef(0);
  const keywordCloudRequestRef = useRef(0);

  const [relatedConceptItems, setRelatedConceptItems] = useState([]);
  const [relatedConceptLoading, setRelatedConceptLoading] = useState(false);
  const [relatedConceptError, setRelatedConceptError] = useState(null);

  const [advancedMetricKey, setAdvancedMetricKey] = useState(
    BENCHMARK_BAR_STATS[0]?.key || "h_index"
  );

  const [isExporting, setIsExporting] = useState(null);

  // ─── Report builder ("Add to report" → PDF) ───────────────────────────────
  // Ordered list of charts/tables the user queued for the export, in the order
  // they were added. Each item is { id, title }.
  const [reportItems, setReportItems] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [captures, setCaptures] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const reportIds = useMemo(
    () => new Set(reportItems.map((i) => i.id)),
    [reportItems]
  );

  const toggleReportItem = useCallback((id, title) => {
    setReportItems((prev) =>
      prev.some((i) => i.id === id)
        ? prev.filter((i) => i.id !== id)
        : [...prev, { id, title }]
    );
  }, []);

  const isOverview = benchmarkMode === BENCHMARK_MODES.RESEARCH_TOPICS_OVERVIEW;
  const entityConfig = ENTITY_PICKER_CONFIG[benchmarkMode];
  const selectedEntityIds = useMemo(
    () => entityIdsByMode[benchmarkMode] || [],
    [entityIdsByMode, benchmarkMode]
  );
  const entityLabelMap = useMemo(
    () => entityLabelMaps[benchmarkMode] || {},
    [entityLabelMaps, benchmarkMode]
  );
  const entityLabel = ENTITY_LABEL_BY_MODE[benchmarkMode];

  // ─── Keyword picker ───────────────────────────────────────────────────────

  // Selected keyword ids are lowercase research-topic names (as sent to the
  // benchmark APIs); chip labels are derived by capitalizing.
  const keywordLabelMap = useMemo(() => {
    const map = {};
    selectedKeywords.forEach((kw) => {
      map[kw] = capitalize(kw);
    });
    return map;
  }, [selectedKeywords]);

  const handleAddKeyword = useCallback((id) => {
    if (id == null) return;
    const normalized = String(id).trim().toLowerCase();
    if (!normalized) return;
    setSelectedKeywords((prev) =>
      prev.includes(normalized) ? prev : [...prev, normalized]
    );
  }, []);

  const handleRemoveKeyword = useCallback((id) => {
    setSelectedKeywords((prev) => prev.filter((k) => k !== id));
  }, []);

  // ─── Entity picker handlers ───────────────────────────────────────────────

  const handleAddEntity = useCallback(
    (id, name) => {
      if (id == null) return;
      const stringId = String(id);
      setEntityIdsByMode((prev) => {
        const current = prev[benchmarkMode] || [];
        if (current.includes(stringId)) return prev;
        return { ...prev, [benchmarkMode]: [...current, stringId] };
      });
      setEntityLabelMaps((prev) => ({
        ...prev,
        [benchmarkMode]: { ...(prev[benchmarkMode] || {}), [stringId]: name },
      }));
    },
    [benchmarkMode]
  );

  const handleRemoveEntity = useCallback(
    (id) => {
      const stringId = String(id);
      setEntityIdsByMode((prev) => ({
        ...prev,
        [benchmarkMode]: (prev[benchmarkMode] || []).filter(
          (v) => v !== stringId
        ),
      }));
    },
    [benchmarkMode]
  );

  // ─── Fetch logic ──────────────────────────────────────────────────────────

  const fetchDashboardContext = useCallback(async (keywords) => {
    if (!keywords || !keywords.length) {
      setDashboardData(null);
      setDashboardError(null);
      setIsDashboardLoading(false);
      return;
    }
    const requestId = dashboardRequestRef.current + 1;
    dashboardRequestRef.current = requestId;
    setIsDashboardLoading(true);
    setDashboardError(null);
    try {
      const res = await getKeywordDashboardContext({ keywords });
      if (dashboardRequestRef.current !== requestId) return;
      setDashboardData(res);
    } catch (err) {
      if (dashboardRequestRef.current !== requestId) return;
      setDashboardError(err);
    } finally {
      if (dashboardRequestRef.current === requestId) {
        setIsDashboardLoading(false);
      }
    }
  }, []);

  const fetchBenchmarkStatistics = useCallback(
    async (mode, keywords, ids, time_filter = "all") => {
      if (!keywords?.length || !ids?.length) {
        setRawData([]);
        setActiveDataMode(mode);
        return;
      }
      const requestId = statsRequestRef.current + 1;
      statsRequestRef.current = requestId;
      setIsLoading(true);
      setError(null);
      try {
        let res;
        if (mode === BENCHMARK_MODES.INSTITUTION) {
          res = await getKeywordStatistics({
            keywords,
            institution_ids: ids,
            time_filter,
          });
        } else if (mode === BENCHMARK_MODES.RESEARCHER) {
          res = await getResearcherBenchmarkStatistics({
            keywords,
            researcher_ids: ids,
            time_filter,
          });
        } else if (mode === BENCHMARK_MODES.UNIT) {
          res = await getUnitBenchmarkStatistics({
            keywords,
            unit_ids: ids,
            time_filter,
          });
        } else if (mode === BENCHMARK_MODES.VENUE) {
          res = await getVenueBenchmarkStatistics({
            keywords,
            venue_ids: ids,
            time_filter,
          });
        } else {
          res = [];
        }
        if (statsRequestRef.current !== requestId) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        setRawData(list);
        setActiveDataMode(mode);
      } catch (err) {
        if (statsRequestRef.current !== requestId) return;
        setError(err);
        setRawData([]);
        setActiveDataMode(mode);
      } finally {
        if (statsRequestRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  // Initial fetch of dashboard context on mount.
  useEffect(() => {
    fetchDashboardContext(initialKeywords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear stale benchmark data + reset request counter when the user switches
  // mode, so the chart area doesn't render data shaped for a previous mode.
  useEffect(() => {
    statsRequestRef.current += 1;
    keywordCloudRequestRef.current += 1;
    setRawData([]);
    setActiveDataMode(null);
    setError(null);
    setIsLoading(false);
    setRelatedConceptItems([]);
    setRelatedConceptError(null);
    setRelatedConceptLoading(false);
  }, [benchmarkMode]);

  // ─── Analyze / export ────────────────────────────────────────────────────

  const handleAnalyze = useCallback(async () => {
    if (!selectedKeywords.length) return;
    if (isOverview) {
      fetchDashboardContext(selectedKeywords);
      return;
    }
    if (!selectedEntityIds.length) return;
    fetchBenchmarkStatistics(
      benchmarkMode,
      selectedKeywords,
      selectedEntityIds,
      timeFilter
    );
  }, [
    selectedKeywords,
    selectedEntityIds,
    benchmarkMode,
    isOverview,
    timeFilter,
    fetchDashboardContext,
    fetchBenchmarkStatistics,
  ]);

  const didMountTimeFilterRef = useRef(false);
  useEffect(() => {
    if (!didMountTimeFilterRef.current) {
      didMountTimeFilterRef.current = true;
      return;
    }
    if (
      !isOverview &&
      activeDataMode === benchmarkMode &&
      selectedKeywords.length &&
      selectedEntityIds.length
    ) {
      fetchBenchmarkStatistics(
        benchmarkMode,
        selectedKeywords,
        selectedEntityIds,
        timeFilter
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter]);

  // ─── Derived chart data ───────────────────────────────────────────────────

  const normalizedRawData = useMemo(
    () =>
      activeDataMode && activeDataMode !== BENCHMARK_MODES.RESEARCH_TOPICS_OVERVIEW
        ? normalizeRawDataForCharts(
            rawData,
            ENTITY_ID_KEY_BY_MODE[activeDataMode]
          )
        : [],
    [rawData, activeDataMode]
  );

  const activeEntityIds = useMemo(() => {
    if (!activeDataMode) return [];
    return entityIdsByMode[activeDataMode] || [];
  }, [activeDataMode, entityIdsByMode]);

  const paletteColors = useMemo(
    () =>
      applyAdminConfig
        ? getPaletteColors(adminConfig.palette)
        : BENCHMARK_INSTITUTION_COLORS,
    [applyAdminConfig, adminConfig]
  );

  const benchmarkChartColorMap = useMemo(() => {
    const overrides = applyAdminConfig ? adminConfig.entityColors || {} : {};
    const map = {};
    activeEntityIds.forEach((id, idx) => {
      map[id] =
        overrides[id] || paletteColors[idx % paletteColors.length];
    });
    return map;
  }, [activeEntityIds, paletteColors, applyAdminConfig, adminConfig]);

  const panelEntities = useMemo(
    () =>
      selectedEntityIds.map((id, idx) => ({
        id,
        name: entityLabelMap[id] || id,
        color:
          (applyAdminConfig && adminConfig.entityColors?.[id]) ||
          paletteColors[idx % paletteColors.length],
      })),
    [
      selectedEntityIds,
      entityLabelMap,
      applyAdminConfig,
      adminConfig,
      paletteColors,
    ]
  );

  // Full metric pool for the advanced-view metric dropdown / heatmap.
  const visibleBarStats = useMemo(
    () => filterMetricsByMode(BENCHMARK_BAR_STATS, activeDataMode),
    [activeDataMode]
  );
  // Core volume metrics for the main bar + pie scaling block.
  const visibleCoreBarStats = useMemo(
    () => filterMetricsByMode(BENCHMARK_CORE_STATS, activeDataMode),
    [activeDataMode]
  );
  const visiblePieStats = useMemo(
    () => filterMetricsByMode(BENCHMARK_PIE_STATS, activeDataMode),
    [activeDataMode]
  );
  const visibleSubCols = useMemo(
    () => filterMetricsByMode(BENCHMARK_SUB_COLS, activeDataMode),
    [activeDataMode]
  );

  const visibleMetricSections = useMemo(() => {
    const hasData = (statList) =>
      statList.some((stat) =>
        normalizedRawData.some((entry) => {
          const kw = Object.keys(entry || {})[0];
          return (entry?.[kw] || []).some((item) => {
            const v = Number(item?.[stat.key]);
            return Number.isFinite(v) && v !== 0;
          });
        })
      );
    const SECTION_CONFIG_KEY = {
      impact: "citationImpact",
      capacity: "capacity",
      momentum: "momentum",
    };
    return BENCHMARK_METRIC_SECTIONS.map((section) => {
      let stats = filterMetricsByMode(section.stats, activeDataMode);
      if (applyAdminConfig) {
        const selected = adminConfig[SECTION_CONFIG_KEY[section.id]] || [];
        stats = stats.filter((s) => selected.includes(s.key));
      }
      return { ...section, stats };
    }).filter((section) => section.stats.length > 0 && hasData(section.stats));
  }, [activeDataMode, normalizedRawData, applyAdminConfig, adminConfig]);

  const scalingView = applyAdminConfig ? adminConfig.scalingView : "both";
  const showHeatmap = applyAdminConfig ? adminConfig.showHeatmap : true;
  const showRelatedConcepts = applyAdminConfig
    ? adminConfig.showRelatedConcepts
    : true;
  const showAdvancedBenchmark = showHeatmap || showRelatedConcepts;
  const showCitationsOverTime =
    !applyAdminConfig || adminConfig.overTime.includes("citations");
  const showPublicationsOverTime =
    !applyAdminConfig || adminConfig.overTime.includes("publications");
  const showGrantsOverTime =
    !applyAdminConfig || adminConfig.overTime.includes("grants");
  const showAdminPanel = !isOverview;

  const breakdownCols = useMemo(
    () =>
      applyAdminConfig
        ? visibleSubCols.filter((c) => adminConfig.breakdown.includes(c.key))
        : visibleSubCols,
    [applyAdminConfig, adminConfig, visibleSubCols]
  );

  // Aggregated per-entity rows for the "table" scaling view.
  const scalingTableRows = useMemo(
    () =>
      scalingView === "table"
        ? aggregateBenchmarkByInstitution(normalizedRawData, activeEntityIds)
        : [],
    [scalingView, normalizedRawData, activeEntityIds]
  );

  const advancedMetric = useMemo(
    () =>
      visibleBarStats.find((m) => m.key === advancedMetricKey) ||
      visibleBarStats[0],
    [advancedMetricKey, visibleBarStats]
  );

  const handleAdvancedMetricChange = useCallback((e) => {
    setAdvancedMetricKey(e.target.value);
  }, []);

  // Snap the selected advanced metric to a still-visible option when the
  // visible set shrinks (e.g. user switches from institution to researcher).
  useEffect(() => {
    if (!visibleBarStats.length) return;
    if (!visibleBarStats.some((m) => m.key === advancedMetricKey)) {
      setAdvancedMetricKey(visibleBarStats[0].key);
    }
  }, [visibleBarStats, advancedMetricKey]);

  useEffect(() => {
    if (!normalizedRawData.length) return undefined;
    const t = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 160);
    return () => window.clearTimeout(t);
  }, [normalizedRawData.length, activeEntityIds.length]);

  const hasActiveData =
    !!activeDataMode &&
    activeDataMode !== BENCHMARK_MODES.RESEARCH_TOPICS_OVERVIEW;

  const publicationByYearSeries = useMemo(() => {
    if (!hasActiveData) return [];
    return collectMetricByYearSeries(
      rawData,
      activeEntityIds,
      ENTITY_ID_KEY_BY_MODE[activeDataMode],
      "publication_count_by_year"
    );
  }, [rawData, activeDataMode, activeEntityIds, hasActiveData]);

  const citationByYearSeries = useMemo(() => {
    if (!hasActiveData) return [];
    return collectMetricByYearSeries(
      rawData,
      activeEntityIds,
      ENTITY_ID_KEY_BY_MODE[activeDataMode],
      "citation_by_year"
    );
  }, [rawData, activeDataMode, activeEntityIds, hasActiveData]);

  // Per-entity aggregate rows for the Output-vs-impact bubble chart.
  const scatterRows = useMemo(
    () =>
      hasActiveData
        ? aggregateBenchmarkByInstitution(normalizedRawData, activeEntityIds)
        : [],
    [hasActiveData, normalizedRawData, activeEntityIds]
  );

  const grantsByYearSeries = useMemo(() => {
    if (!hasActiveData || activeDataMode === BENCHMARK_MODES.VENUE) return [];
    return collectMetricByYearSeries(
      rawData,
      activeEntityIds,
      ENTITY_ID_KEY_BY_MODE[activeDataMode],
      "grants_count_by_year"
    );
  }, [rawData, activeDataMode, activeEntityIds, hasActiveData]);

  // ─── Validation flags ─────────────────────────────────────────────────────

  const canAnalyze = isOverview
    ? selectedKeywords.length > 0
    : selectedKeywords.length > 0 && selectedEntityIds.length > 0;

  const hasBenchmarkRender =
    activeDataMode &&
    activeDataMode !== BENCHMARK_MODES.RESEARCH_TOPICS_OVERVIEW &&
    normalizedRawData.length > 0 &&
    activeEntityIds.length > 0;

  // Selected entity display names (for the report header).
  const selectedEntityNames = useMemo(
    () =>
      activeEntityIds.map(
        (eid) => entityLabelMaps[activeDataMode]?.[eid] || eid
      ),
    [activeEntityIds, entityLabelMaps, activeDataMode]
  );

  // Builds the legend + statistics table that accompany a captured chart in
  // the PDF/preview. Keyed by the chart's report id. Uses the same derived
  // data (and colours) the on-screen charts render from.
  const buildReportExtras = useCallback(
    (id) => {
      const entities = activeEntityIds.map((eid) => ({
        id: eid,
        name: entityLabelMaps[activeDataMode]?.[eid] || eid,
        color: benchmarkChartColorMap[eid],
      }));
      const legend = entities.map((e) => ({ name: e.name, color: e.color }));

      const agg = aggregateBenchmarkByInstitution(
        normalizedRawData,
        activeEntityIds
      );
      const aggById = {};
      agg.forEach((r) => {
        aggById[r.institution_id] = r;
      });

      const statTable = (stats) => ({
        columns: ["Entity", ...stats.map((s) => s.label)],
        rows: entities.map((e) => [
          e.name,
          ...stats.map((s) =>
            formatBenchmarkStatValue(aggById[e.id]?.[s.key], s)
          ),
        ]),
      });

      if (id === "metric-scaling") {
        // The scaling chart already renders its own entity legend.
        return { legend: null, table: statTable(visibleCoreBarStats) };
      }
      if (id.startsWith("section-")) {
        const section = visibleMetricSections.find(
          (s) => `section-${s.id}` === id
        );
        return { legend, table: section ? statTable(section.stats) : null };
      }
      if (id === "output-vs-impact") {
        return {
          legend,
          table: {
            columns: [
              "Entity",
              "Publications",
              "Citations / publication",
              "h-index",
            ],
            rows: entities.map((e) => {
              const r = aggById[e.id] || {};
              const pubs = Number(r.publication_count) || 0;
              const cites = Number(r.citation_count) || 0;
              const cpp = pubs > 0 ? cites / pubs : 0;
              return [
                e.name,
                pubs.toLocaleString(),
                cpp.toLocaleString(undefined, { maximumFractionDigits: 1 }),
                (Number(r.h_index) || 0).toLocaleString(),
              ];
            }),
          },
        };
      }

      const seriesByReportId = {
        "citations-over-time": citationByYearSeries,
        "publications-over-time": publicationByYearSeries,
        "grants-over-time": grantsByYearSeries,
      };
      if (seriesByReportId[id]) {
        const series = seriesByReportId[id];
        const years = Array.from(
          new Set(series.flatMap((s) => s.points.map((p) => p.year)))
        ).sort((a, b) => a - b);
        return {
          legend: series.map((s) => ({
            name: s.name,
            color: benchmarkChartColorMap[s.id],
          })),
          table: {
            columns: ["Year", ...series.map((s) => s.name)],
            // Narrow the Year column; give the entity columns the rest.
            colWidths: [0.55, ...series.map(() => 1.5)],
            rows: years.map((yr) => [
              String(yr),
              ...series.map((s) => {
                const p = s.points.find((pt) => pt.year === yr);
                return p ? p.value.toLocaleString() : "—";
              }),
            ]),
          },
        };
      }

      if (id === "heatmap" && advancedMetric) {
        return {
          legend,
          table: {
            columns: [
              `Topic · ${advancedMetric.label}`,
              ...entities.map((e) => e.name),
            ],
            rows: normalizedRawData.map((entry) => {
              const kw = Object.keys(entry || {})[0];
              const byId = {};
              (entry?.[kw] || []).forEach((it) => {
                byId[it.institution_id] = it;
              });
              return [
                capitalize(kw || ""),
                ...entities.map((e) =>
                  formatBenchmarkStatValue(
                    byId[e.id]?.[advancedMetric.key],
                    advancedMetric
                  )
                ),
              ];
            }),
          },
        };
      }

      if (id === "related-concepts") {
        const items = (relatedConceptItems || []).slice(0, 15);
        return {
          legend: null,
          table: items.length
            ? {
                columns: ["Related concept", "Score"],
                rows: items.map((it) => [
                  it.value || it.name || "—",
                  it.score != null
                    ? Number(it.score).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : "—",
                ]),
              }
            : null,
        };
      }

      if (id === "top-faculty") {
        const rows = [];
        const entityName = ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
          "institution";
        normalizedRawData.forEach((entry) => {
          const kw = Object.keys(entry || {})[0];
          const merged = new Map();
          (entry?.[kw] || []).forEach((inst) => {
            const instName = inst.name || "—";
            (inst.faculty_publication_counts || []).forEach((f) => {
              if (!f?.id) return;
              const n = Number(f.publication_count) || 0;
              const prev = merged.get(f.id);
              if (prev) {
                prev.count += n;
                // Attribute to the institution contributing the most.
                if (n > prev.topInstCount) {
                  prev.topInstCount = n;
                  prev.institution = instName;
                }
              } else {
                merged.set(f.id, {
                  name: f.name || "—",
                  count: n,
                  institution: instName,
                  topInstCount: n,
                });
              }
            });
          });
          const topicLabel = capitalize(kw || "");
          Array.from(merged.values())
            .sort((a, b) => b.count - a.count)
            .forEach((f, i) => {
              rows.push([
                i === 0 ? topicLabel : "",
                f.name,
                f.institution,
                f.count.toLocaleString(),
              ]);
            });
        });
        return {
          legend: null,
          table: rows.length
            ? {
                columns: [
                  "Research topic",
                  "Faculty",
                  capitalize(entityName),
                  "Publications",
                ],
                // Narrow topic + publications; widen the institution column.
                colWidths: [0.95, 1.25, 1.9, 0.75],
                rows,
              }
            : null,
        };
      }

      // Breakdown table: the captured image is already the full table; its
      // entity columns are labelled, so no separate legend is needed.
      return { legend: null, table: null };
    },
    [
      activeEntityIds,
      entityLabelMaps,
      activeDataMode,
      benchmarkChartColorMap,
      normalizedRawData,
      visibleCoreBarStats,
      visibleMetricSections,
      citationByYearSeries,
      publicationByYearSeries,
      grantsByYearSeries,
      advancedMetric,
      relatedConceptItems,
    ]
  );

  const canExportReport =
    reportItems.length > 0 && !isCapturing && isExporting !== "pdf";

  const timeRangeLabel = useMemo(
    () =>
      TIME_FILTER_OPTIONS.find((o) => o.value === timeFilter)?.label || "",
    [timeFilter]
  );
  const modeLabel = useMemo(
    () => MODE_OPTIONS.find((m) => m.value === benchmarkMode)?.label || "",
    [benchmarkMode]
  );

  const openReportPreview = useCallback(async () => {
    if (!reportItems.length) return;
    setPreviewOpen(true);
    setIsCapturing(true);
    setCaptures([]);
    try {
      const caps = await captureReportItems(
        reportItems.filter((i) => !REPORT_NO_CAPTURE_IDS.has(i.id))
      );
      const capById = {};
      caps.forEach((c) => {
        capById[c.id] = c;
      });
      setCaptures(
        reportItems.map((item) => ({
          id: item.id,
          title: item.title,
          ...(capById[item.id] || {}),
          ...buildReportExtras(item.id),
        }))
      );
    } catch (e) {
      setCaptures([]);
    } finally {
      setIsCapturing(false);
    }
  }, [reportItems, buildReportExtras]);

  const closeReportPreview = useCallback(() => {
    setPreviewOpen(false);
    setCaptures([]);
  }, []);

  const handleConfirmExport = useCallback(() => {
    if (!captures.length) return;
    setIsExporting("pdf");
    try {
      exportReportPdf({
        captures,
        selectedKeywords,
        selectedEntities: selectedEntityNames,
        entityNoun: capitalize(
          ENTITY_LABEL_BY_MODE[activeDataMode]?.plural || "entities"
        ),
        modeLabel,
        timeRangeLabel,
      });
    } finally {
      setIsExporting(null);
      closeReportPreview();
    }
  }, [
    captures,
    selectedKeywords,
    selectedEntityNames,
    activeDataMode,
    modeLabel,
    timeRangeLabel,
    closeReportPreview,
  ]);

  const canExportExcel =
    !isOverview &&
    activeDataMode === benchmarkMode &&
    hasBenchmarkRender &&
    !isLoading &&
    !error &&
    !isExporting;

  const handleExportExcel = useCallback(async () => {
    if (!canExportExcel) return;
    setIsExporting("excel");
    try {
      await exportBenchmarkPairBreakdownExcel({
        mode: benchmarkMode,
        rawData,
        entityIds: selectedEntityIds,
        entityNames: entityLabelMaps[benchmarkMode] || {},
      });
    } finally {
      setIsExporting(null);
    }
  }, [
    canExportExcel,
    benchmarkMode,
    selectedEntityIds,
    entityLabelMaps,
    rawData,
  ]);

  const selectedPptxCount = useMemo(
    () => reportItems.filter((i) => PPTX_REPORT_IDS.has(i.id)).length,
    [reportItems]
  );
  const canExportPptx =
    selectedPptxCount > 0 && hasBenchmarkRender && !isLoading && !error;

  const handleExportPptx = useCallback(async () => {
    if (!canExportPptx || isExporting) return;
    setIsExporting("pptx");
    try {
      let fetchedAnchors = null;
      try {
        const anchorsData = await getReportAnchors();
        fetchedAnchors = anchorsData?.anchors;
      } catch (anchorErr) {
        console.warn("[pptx] anchors fetch failed, using defaults", anchorErr);
      }
      const anchors = { ...DEFAULT_PPTX_ANCHORS, ...(fetchedAnchors || {}) };

      const entitySingular =
        ENTITY_LABEL_BY_MODE[activeDataMode]?.singular || "entity";
      const legendRow = (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px 20px",
            marginBottom: 14,
          }}
        >
          {activeEntityIds.map((id) => (
            <span
              key={id}
              style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: benchmarkChartColorMap[id],
                }}
              />
              <span style={{ fontSize: 15, color: "#334155" }}>
                {entityLabelMaps[activeDataMode]?.[id] || id}
              </span>
            </span>
          ))}
        </div>
      );
      const exportChartElement = (reportId) => {
        switch (reportId) {
          case "metric-scaling":
            // Vertical list of bar groups → match width, natural height.
            return {
              box: true,
              element: (
                <div style={{ padding: "8px 4px" }}>
                  {legendRow}
                  <StackedBarChart
                    rawData={normalizedRawData}
                    institutionIds={activeEntityIds}
                    showHeader={false}
                    stats={visibleCoreBarStats}
                    colorMap={benchmarkChartColorMap}
                  />
                </div>
              ),
            };
          case "output-vs-impact":
            return {
              box: true,
              element: (
                <BenchmarkScatterChart
                  benchmarkRows={scatterRows}
                  colorMap={benchmarkChartColorMap}
                  exportMode
                />
              ),
            };
          case "heatmap":
            return advancedMetric
              ? {
                  box: true,
                  element: (
                    <BenchmarkHeatmap
                      rawData={normalizedRawData}
                      institutionIds={activeEntityIds}
                      metricKey={advancedMetric.key}
                      metricLabel={advancedMetric.label}
                      metric={advancedMetric}
                      entityLabel={capitalize(entitySingular)}
                      exportMode
                    />
                  ),
                }
              : null;
          case "citations-over-time":
            return {
              box: true,
              element: (
                <CitationByYearChart
                  series={citationByYearSeries}
                  colorMap={benchmarkChartColorMap}
                  entityLabel={entitySingular}
                  title="Citations over time"
                  exportMode
                />
              ),
            };
          case "publications-over-time":
            return {
              box: true,
              element: (
                <CitationByYearChart
                  series={publicationByYearSeries}
                  colorMap={benchmarkChartColorMap}
                  entityLabel={entitySingular}
                  title="Publications over time"
                  exportMode
                />
              ),
            };
          case "grants-over-time":
            return {
              box: true,
              element: (
                <CitationByYearChart
                  series={grantsByYearSeries}
                  colorMap={benchmarkChartColorMap}
                  entityLabel={entitySingular}
                  title="Grants over time"
                  exportMode
                />
              ),
            };
          case "breakdown":
            return {
              box: false,
              element: (
                <BreakdownTable
                  rawData={rawData}
                  institutionIds={activeEntityIds}
                  layout="entityRows"
                  entityIdKey={ENTITY_ID_KEY_BY_MODE[activeDataMode]}
                  entityLabel={capitalize(entitySingular)}
                  subCols={breakdownCols}
                />
              ),
            };
          default:
            return null;
        }
      };

      const files = {};
      for (const [code, spec] of Object.entries(anchors)) {
        if (spec?.type && spec.type !== "IMAGE") continue;
        const reportId = PPTX_CHART_CODE_TO_REPORT_ID[code.toUpperCase()];
        if (!reportId || !reportIds.has(reportId)) continue;
        const built = exportChartElement(reportId);
        const blob = built
          ? await captureReactAtSize(built.element, {
              widthPx: spec?.width_px,
              heightPx: built.box ? spec?.height_px : undefined,
            })
          : await captureReportElement(reportId, {
              targetWidthPx: spec?.width_px,
              targetHeightPx: spec?.height_px,
            });
        if (blob) files[code] = blob;
      }

      for (const [code, reportId] of Object.entries(
        PPTX_TABLE_CODE_TO_REPORT_ID
      )) {
        if (!reportIds.has(reportId)) continue;
        const table = buildReportExtras(reportId)?.table;
        if (!table?.columns?.length || !table?.rows?.length) continue;
        const wPx = anchors[code]?.width_px;
        const hPx = anchors[code]?.height_px;
        const CAPTURE_SCALE = 2;
        const blob = await captureReactAtSize(
          <ExportTable
            columns={table.columns}
            rows={table.rows}
            width={wPx ? wPx / CAPTURE_SCALE : 0}
            height={hPx ? hPx / CAPTURE_SCALE : 0}
          />,
          { widthPx: wPx, heightPx: hPx }
        );
        if (blob) files[code] = blob;
      }

      // Top faculty: one card image per picked keyword (TOP_FACULTY_1..N).
      if (reportIds.has(PPTX_TOP_FACULTY_REPORT_ID)) {
        let kwIndex = 0;
        for (const entry of normalizedRawData) {
          kwIndex += 1;
          const kw = Object.keys(entry || {})[0];
          if (!kw) continue;
          const merged = new Map();
          (entry[kw] || []).forEach((inst) => {
            (inst.faculty_publication_counts || []).forEach((f) => {
              if (!f?.id) return;
              const prev = merged.get(f.id);
              const n = Number(f.publication_count) || 0;
              if (prev) {
                prev.count += n;
              } else {
                merged.set(f.id, {
                  name: f.name || "—",
                  photo_url: f.photo_url || null,
                  count: n,
                });
              }
            });
          });
          const top = Array.from(merged.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, 12);
          if (!top.length) continue;
          const code = pptxTopFacultyCode(kwIndex);
          // eslint-disable-next-line no-await-in-loop
          const blob = await captureFacultyCardPng({
            title: kw.charAt(0).toUpperCase() + kw.slice(1),
            faculty: top,
            targetWidthPx: anchors[code]?.width_px,
            targetHeightPx: anchors[code]?.height_px,
          });
          if (blob) files[code] = blob;
        }
      }

      // Metric sections (Citation Impact / Capacity & Productivity / Momentum &
      // Reach): each section exposes an ordered set of slots (4, 2, 2). When a
      // section is selected, fill its slots with the user's chosen metrics (in
      // order, up to the slot count). Each slot uploads a single-metric bar
      // chart image under `${prefix}_${i}` and sends the metric's display name
      // as text under `${prefix}_${i}_NAME` (payload.blocks).
      const blocks = {};
      const anySectionSelected = Object.keys(PPTX_METRIC_SECTIONS).some((id) =>
        reportIds.has(`section-${id}`)
      );
      if (anySectionSelected) {
        for (const section of visibleMetricSections) {
          const sectionCfg = PPTX_METRIC_SECTIONS[section.id];
          if (!sectionCfg) continue;
          if (!reportIds.has(`section-${section.id}`)) continue;
          const { prefix, slots } = sectionCfg;
          // The user picks up to `slots` charts per section; take them in order.
          const chosen = section.stats.slice(0, slots);
          for (let i = 0; i < chosen.length; i += 1) {
            const stat = chosen[i];
            const imageCode = pptxSectionImageCode(prefix, i + 1);
            const nameCode = pptxSectionNameCode(prefix, i + 1);
            const spec = anchors[imageCode];
            const blob = await captureReactAtSize(
              <GroupedMetricBars
                rawData={normalizedRawData}
                institutionIds={activeEntityIds}
                stats={[stat]}
                colorMap={benchmarkChartColorMap}
                exportMode
              />,
              { widthPx: spec?.width_px, heightPx: spec?.height_px }
            );
            if (blob) {
              files[imageCode] = blob; // chart image (FormData part)
              blocks[nameCode] = stat.label; // metric name (payload.blocks text)
            }
          }
        }
      }

      if (!Object.keys(files).length) {
        console.warn(
          "[pptx] nothing to export — selected report ids:",
          Array.from(reportIds)
        );
        message.error(
          "None of the selected charts are on screen. Scroll them into view, then try again."
        );
        return;
      }

      const entityType = ENTITY_LABEL_BY_MODE[activeDataMode]?.plural;
      const payload = {
        template: REPORT_TEMPLATE,
        keywords: selectedKeywords,
        title: `${selectedKeywords.map(capitalize).join(", ")} — Benchmark report`,
        ...(entityType ? { entity_type: entityType } : {}),
        ...(activeEntityIds.length
          ? { selected_entity_ids: activeEntityIds }
          : {}),
        ...(Object.keys(blocks).length ? { blocks } : {}),
      };

      const fileBlob = await exportBenchmarkReport({
        payload,
        files,
        time_filter: timeFilter,
      });

      const url = URL.createObjectURL(fileBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `benchmark_report_${Date.now()}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success("PowerPoint report downloaded.");
    } catch (e) {
      console.error("[pptx] export failed", e);
      message.error(
        "Could not generate the PowerPoint report. Please try again."
      );
    } finally {
      setIsExporting(null);
    }
  }, [
    canExportPptx,
    isExporting,
    reportIds,
    buildReportExtras,
    normalizedRawData,
    activeEntityIds,
    activeDataMode,
    entityLabelMaps,
    visibleMetricSections,
    visibleCoreBarStats,
    benchmarkChartColorMap,
    scatterRows,
    advancedMetric,
    citationByYearSeries,
    publicationByYearSeries,
    grantsByYearSeries,
    breakdownCols,
    rawData,
    selectedKeywords,
    timeFilter,
  ]);

  useEffect(() => {
    if (!hasBenchmarkRender) {
      setRelatedConceptItems([]);
      setRelatedConceptError(null);
      setRelatedConceptLoading(false);
      return;
    }
    const objectType = objectTypeFromBenchmarkMode(benchmarkMode);
    if (!objectType || !selectedKeywords.length || !selectedEntityIds.length) {
      setRelatedConceptItems([]);
      return;
    }

    const requestId = keywordCloudRequestRef.current + 1;
    keywordCloudRequestRef.current = requestId;
    setRelatedConceptLoading(true);
    setRelatedConceptError(null);

    getBenchmarkKeywordCloud({
      keywords: selectedKeywords,
      objectIds: selectedEntityIds,
      objectType,
      verify: true,
      granularityLevel: 2,
    })
      .then((data) => {
        if (keywordCloudRequestRef.current !== requestId) return;
        const list = data?.keywords ?? data?.data?.keywords ?? [];
        setRelatedConceptItems(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (keywordCloudRequestRef.current !== requestId) return;
        setRelatedConceptError(new Error("keyword_cloud"));
        setRelatedConceptItems([]);
      })
      .finally(() => {
        if (keywordCloudRequestRef.current === requestId) {
          setRelatedConceptLoading(false);
        }
      });
  }, [
    hasBenchmarkRender,
    benchmarkMode,
    selectedKeywords,
    selectedEntityIds,
  ]);

  const handleRelatedConceptClick = useCallback(
    (label) => {
      const normalized = String(label).trim().toLowerCase();
      if (!normalized || selectedKeywords.includes(normalized)) return;
      const next = [...selectedKeywords, normalized];
      setSelectedKeywords(next);
      fetchBenchmarkStatistics(
        benchmarkMode,
        next,
        selectedEntityIds,
        timeFilter
      );
    },
    [
      benchmarkMode,
      selectedKeywords,
      selectedEntityIds,
      timeFilter,
      fetchBenchmarkStatistics,
    ]
  );

  const renderEmptyState = (title, message) => (
    <div className={styles["empty-chart-state"]}>
      <div className={styles["empty-chart-state__icon"]} aria-hidden>
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 3v18h18" />
          <path d="M7 16V9" />
          <path d="M12 16v-4" />
          <path d="M17 16v-6" />
        </svg>
      </div>
      <h3 className={styles["empty-chart-state__title"]}>{title}</h3>
      <p className={styles["empty-chart-state__text"]}>{message}</p>
    </div>
  );

  // "Table" option for the metric-scaling view: entities × core metrics.
  const renderScalingTable = () => {
    const cols = filterMetricsByMode(BENCHMARK_CORE_STATS, activeDataMode);
    if (!scalingTableRows.length || !cols.length) return null;
    const entitySingular = capitalize(
      ENTITY_LABEL_BY_MODE[activeDataMode]?.singular || "entity"
    );
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={SCALING_TABLE_TH}>{entitySingular}</th>
              {cols.map((c) => (
                <th key={c.key} style={{ ...SCALING_TABLE_TH, textAlign: "right" }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scalingTableRows.map((row) => (
              <tr key={row.institution_id}>
                <td style={SCALING_TABLE_TD}>{row.name}</td>
                {cols.map((c) => (
                  <td
                    key={c.key}
                    style={{ ...SCALING_TABLE_TD, textAlign: "right" }}
                  >
                    {formatBenchmarkStatValue(row[c.key], c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className={styles["results"]}
      style={
        !isNarrow && entityConfig ? { paddingRight: 332 } : undefined
      }
    >
      {showAdminPanel ? (
        <AdminBenchmarkPanel
          config={adminConfig}
          onChange={handleAdminConfigChange}
          entities={panelEntities}
          canSave={isLoggedIn}
          currentVersion={configVersion}
          currentLabel={previewVersion != null ? null : configLabel}
          saving={savingVersion}
          onSave={openSaveModal}
          onOpenVersions={() => loadVersions(0)}
          versions={versions}
          versionsTotal={versionsTotal}
          versionsPage={versionsPage}
          versionsLimit={VERSIONS_LIMIT}
          versionsLoading={versionsLoading}
          previewVersion={previewVersion}
          reverting={reverting}
          onLoadVersions={loadVersions}
          onPreviewVersion={handlePreviewVersion}
          onRevertVersion={handleRevertVersion}
          panelStyle={
            isNarrow
              ? undefined
              : {
                  // Sit directly below the fixed 400px entity panel.
                  top: `calc(var(--navbar-height, 60px) + ${headerHeight}px + 16px + 300px + 16px)`,
                  bottom: 16,
                  maxHeight: "none",
                }
          }
        />
      ) : null}

      {/* Fixed pair-entity panel (wide screens) — mirrors the customize panel */}
      {!isNarrow && entityConfig ? (
        <aside
          className={styles["pair-panel"]}
          style={{
            position: "fixed",
            right: 16,
            top: `calc(var(--navbar-height, 60px) + ${headerHeight}px + 16px)`,
            width: 300,
            height: 300,
            zIndex: 1300,
            overflowY: "auto",
          }}
          aria-label={entityConfig.label}
        >
          <label className={styles["filter-label"]}>{entityConfig.label}</label>
          <BenchmarkEntityPicker
            selectedIds={selectedEntityIds}
            labelMap={entityLabelMap}
            onAdd={handleAddEntity}
            onRemove={handleRemoveEntity}
            functionSearch={entityConfig.functionSearch}
            valueField={entityConfig.valueField}
            placeholder={entityConfig.placeholder}
            emptyHint={entityConfig.emptyHint}
          />
        </aside>
      ) : null}
      {/* ─── Fixed header: research topics + time range + actions ─── */}
      <header
        ref={headerRef}
        className={styles["topbar"]}
        style={isNarrow ? undefined : { left: contentLeftInset }}
      >
        <div className={styles["topbar__topics"]}>
          <label className={styles["filter-label"]}>Research Topics</label>
          <BenchmarkEntityPicker
            selectedIds={selectedKeywords}
            labelMap={keywordLabelMap}
            onAdd={handleAddKeyword}
            onRemove={handleRemoveKeyword}
            functionSearch={getSuggestKeywords}
            valueField="name"
            placeholder="Search keywords..."
            emptyHint="Type at least 2 characters to search."
          />
        </div>

        <div className={styles["topbar__controls"]}>
          {!isOverview ? (
            <div className={styles["topbar__group"]}>
              <label className={styles["filter-label"]}>Time Range</label>
              <div
                className={styles["timeseg"]}
                role="group"
                aria-label="Time range"
              >
                {TIME_FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles["timeseg__btn"]} ${
                      timeFilter === opt.value
                        ? styles["timeseg__btn--active"]
                        : ""
                    }`}
                    onClick={() => {
                      if (opt.value === timeFilter) return;
                      setTimeFilter(opt.value)
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className={styles["topbar__actions"]}>
            <button
              className={styles["analyze-btn"]}
              onClick={() => handleAnalyze()}
              disabled={!canAnalyze || isLoading || isDashboardLoading}
            >
              <AutorenewIcon
                sx={{ fontSize: 18 }}
                className={isLoading || isDashboardLoading ? styles["spin"] : ""}
              />
              {isLoading || isDashboardLoading ? "Analyzing..." : "Analyze"}
            </button>

            <Tooltip
              title={
                reportItems.length
                  ? `Preview & export ${reportItems.length} selected item${
                      reportItems.length > 1 ? "s" : ""
                    }`
                  : "Add charts to the report first"
              }
              placement="top"
            >
              <span>
                <button
                  type="button"
                  className={styles["export-btn"]}
                  disabled={!canExportReport}
                  onClick={openReportPreview}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                  PDF{reportItems.length ? ` (${reportItems.length})` : ""}
                </button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                selectedPptxCount > 0
                  ? `Export ${selectedPptxCount} selected chart${
                      selectedPptxCount > 1 ? "s" : ""
                    } to PowerPoint`
                  : "Add Metric Scaling, Output vs. Impact, or Citations Over Time to the report first"
              }
              placement="top"
            >
              <span>
                <button
                  type="button"
                  className={styles["export-btn"]}
                  disabled={!canExportPptx || isExporting === "pptx"}
                  onClick={handleExportPptx}
                >
                  <SlideshowIcon sx={{ fontSize: 16 }} />
                  {isExporting === "pptx"
                    ? "Building…"
                    : `PPTX${selectedPptxCount ? ` (${selectedPptxCount})` : ""}`}
                </button>
              </span>
            </Tooltip>

            {/* Excel export temporarily hidden. To restore, un-comment below.
            <span>
              <button
                type="button"
                className={`${styles["export-btn"]} ${styles["export-btn--excel"]}`}
                disabled={!canExportExcel}
                onClick={handleExportExcel}
              >
                <TableChartIcon sx={{ fontSize: 16 }} />
                Excel
              </button>
            </span>
            */}
          </div>
        </div>
      </header>

      {/* ─── Workspace: fixed mode rail + results column ─── */}
      <div
        className={styles["workspace"]}
        style={
          isNarrow
            ? undefined
            : {
                paddingTop: headerHeight + 24,
                paddingLeft: railWidth + 44,
                paddingBottom: 0,
              }
        }
      >
        {/* Fixed, collapsible benchmark-mode rail */}
        <nav
          className={`${styles["rail"]} ${
            railCollapsed ? styles["rail--collapsed"] : ""
          }`}
          aria-label="Benchmark mode"
          style={
            isNarrow
              ? undefined
              : {
                  left: contentLeftInset + 20,
                  top: `calc(var(--navbar-height, 60px) + ${headerHeight}px + 16px)`,
                  height: `calc(100vh - var(--navbar-height, 60px) - ${headerHeight}px - 40px)`,
                }
          }
        >
          <div className={styles["rail__head"]}>
            {!railCollapsed ? (
              <span className={styles["rail__eyebrow"]}>Benchmark Mode</span>
            ) : null}
            <button
              type="button"
              className={styles["rail__toggle"]}
              onClick={() => setRailCollapsed((c) => !c)}
              aria-label={railCollapsed ? "Expand menu" : "Collapse menu"}
              title={railCollapsed ? "Expand" : "Collapse"}
            >
              {railCollapsed ? "»" : "«"}
            </button>
          </div>
          <div className={styles["rail__list"]}>
            {MODE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles["rail__item"]} ${
                  benchmarkMode === opt.value
                    ? styles["rail__item--active"]
                    : ""
                }`}
                onClick={() => {
                  if (opt.value === benchmarkMode) return;
                  setBenchmarkMode(opt.value)
                }}
                title={opt.label}
                aria-current={benchmarkMode === opt.value}
              >
                <span className={styles["rail__icon"]}>
                  {MODE_ICONS[opt.value]}
                </span>
                {!railCollapsed ? (
                  <span className={styles["rail__label"]}>{opt.label}</span>
                ) : null}
              </button>
            ))}
          </div>
        </nav>

        {/* Results column: pair-entity picker on its own row, then the charts.
            On wide screens this is the only scroll container — a fixed height
            bounded to the viewport keeps content from sliding under the fixed
            header/rail. */}
        <div
          className={styles["content-col"]}
          style={
            isNarrow
              ? undefined
              : {
                  height: `calc(100vh - var(--navbar-height, 60px) - ${headerHeight}px - 48px)`,
                  overflowY: "auto",
                }
          }
        >
        {/* Pair-entity picker — narrow screens only (wide uses the fixed panel) */}
        {isNarrow && entityConfig ? (
          <aside
            className={styles["pair-panel"]}
            aria-label={entityConfig.label}
          >
            <label className={styles["filter-label"]}>
              {entityConfig.label}
            </label>
            <BenchmarkEntityPicker
              selectedIds={selectedEntityIds}
              labelMap={entityLabelMap}
              onAdd={handleAddEntity}
              onRemove={handleRemoveEntity}
              functionSearch={entityConfig.functionSearch}
              valueField={entityConfig.valueField}
              placeholder={entityConfig.placeholder}
              emptyHint={entityConfig.emptyHint}
            />
          </aside>
        ) : null}

        {/* ─── Main content ─── */}
        <main className={styles["main"]}>
          {isOverview ? (
            <>
              <DashboardContextSection
                data={dashboardData}
                isLoading={isDashboardLoading}
                error={dashboardError}
                selectedKeywords={selectedKeywords}
              />
              {!isDashboardLoading && !dashboardError && dashboardData ? (
                <ResearchTopicsRankingView
                  data={dashboardData}
                  selectedKeywords={selectedKeywords}
                  keywordLabelMap={keywordLabelMap}
                />
              ) : null}
            </>
          ) : (
            <section className={styles["benchmark-results-section"]}>
              <div className={styles["section-head"]}>
                <span className={styles["section-eyebrow"]}>
                  Benchmark Results
                </span>
                <h2 className={styles["section-title"]}>
                  Compare {entityLabel?.plural || "entities"} side by side
                </h2>
                <p className={styles["section-subtitle"]}>
                  Add {entityLabel?.plural || "entities"} to power the charts,
                  advanced benchmark views, and breakdown table.
                </p>
              </div>

              <div className={styles["results-wrapper"]}>
                {isLoading ? (
                  <div className={styles["skeleton-wrapper"]}>
                    <Skeleton paragraph={{ rows: 8 }} active />
                  </div>
                ) : error ? (
                  <div className={styles["error-msg"]}>
                    <div className={styles["error-msg__icon"]}>⚠</div>
                    <div className={styles["error-msg__text"]}>
                      Unable to load data. Please try again.
                    </div>
                  </div>
                ) : !selectedEntityIds.length ? (
                  renderEmptyState(
                    `Choose ${entityLabel?.plural || "entities"} to see the comparison`,
                    `Use the “${entityConfig?.label || "search"}” box above to add one or more ${
                      entityLabel?.plural || "entities"
                    }, then click Analyze to populate the benchmark charts and breakdown table.`
                  )
                ) : !hasBenchmarkRender ? (
                  renderEmptyState(
                    "Run the analysis to see results",
                    "Click Analyze to fetch the benchmark statistics for the selected research topics and entities."
                  )
                ) : (
                  <>
                    {(activeDataMode === BENCHMARK_MODES.INSTITUTION ||
                      activeDataMode === BENCHMARK_MODES.UNIT) ? (
                      <ReportableChart
                        id="top-faculty"
                        title="Top faculty by research topic"
                        selected={reportIds.has("top-faculty")}
                        onToggle={toggleReportItem}
                      >
                        <KeywordTopFacultySection
                          rawData={normalizedRawData}
                          entityLabel={
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.plural ||
                            "institutions"
                          }
                          barColor={
                            benchmarkChartColorMap[activeEntityIds[0]] ||
                            paletteColors[0]
                          }
                        />
                      </ReportableChart>
                    ) : null}

                    <ReportableChart
                      id="metric-scaling"
                      title={`Inter-${capitalize(
                        entityLabel?.singular || "entity"
                      )} metric scaling`}
                      selected={reportIds.has("metric-scaling")}
                      onToggle={toggleReportItem}
                    >
                    <div className={barChartStyles["stacked-chart"]}>
                      <div className={styles["benchmark-scaling-head"]}>
                        <h3
                          className={barChartStyles["stacked-chart__title"]}
                          data-report-strip="true"
                        >
                          Inter-{capitalize(entityLabel?.singular || "entity")} Metric Scaling
                        </h3>
                        <div className={styles["benchmark-scaling-legend"]}>
                          {activeEntityIds.map((id) => (
                            <div
                              key={id}
                              className={styles["benchmark-scaling-legend__item"]}
                            >
                              <div
                                className={styles["benchmark-scaling-legend__swatch"]}
                                style={{
                                  backgroundColor: benchmarkChartColorMap[id],
                                }}
                              />
                              <span
                                className={styles["benchmark-scaling-legend__name"]}
                              >
                                {entityLabelMaps[activeDataMode]?.[id] || id}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {scalingView === "both" ? (
                        <div
                          aria-label="Bar and pie chart comparison"
                          className={styles["scaling-chart-grid"]}
                        >
                          <div className={styles["scaling-chart-block"]}>
                            <div className={styles["chart-viz-tabpane"]}>
                              <StackedBarChart
                                rawData={normalizedRawData}
                                institutionIds={activeEntityIds}
                                showHeader={false}
                                stats={visibleCoreBarStats}
                                colorMap={benchmarkChartColorMap}
                              />
                            </div>
                          </div>
                          <div className={styles["scaling-chart-block"]}>
                            <div className={styles["chart-viz-tabpane"]}>
                              <PieChart
                                rawData={normalizedRawData}
                                institutionIds={activeEntityIds}
                                showHeader={false}
                                stats={visiblePieStats}
                                colorMap={benchmarkChartColorMap}
                              />
                            </div>
                          </div>
                        </div>
                      ) : scalingView === "bar" ? (
                        <div className={styles["chart-viz-tabpane"]}>
                          <StackedBarChart
                            rawData={normalizedRawData}
                            institutionIds={activeEntityIds}
                            showHeader={false}
                            stats={visibleCoreBarStats}
                            colorMap={benchmarkChartColorMap}
                          />
                        </div>
                      ) : scalingView === "pie" ? (
                        <div className={styles["chart-viz-tabpane"]}>
                          <PieChart
                            rawData={normalizedRawData}
                            institutionIds={activeEntityIds}
                            showHeader={false}
                            stats={visiblePieStats}
                            colorMap={benchmarkChartColorMap}
                          />
                        </div>
                      ) : (
                        renderScalingTable()
                      )}
                    </div>
                    </ReportableChart>

                    {scatterRows.length > 0 ? (
                      <ReportableChart
                        id="output-vs-impact"
                        title="Output vs. impact"
                        selected={reportIds.has("output-vs-impact")}
                        onToggle={toggleReportItem}
                      >
                        <LazyMountSection minHeight={580}>
                          <BenchmarkScatterChart
                            benchmarkRows={scatterRows}
                            colorMap={benchmarkChartColorMap}
                          />
                        </LazyMountSection>
                      </ReportableChart>
                    ) : null}

                    {/* Grouped metric sections (Citation Impact / Capacity /
                        Momentum) — each reuses the bar chart with a metric
                        subset and the shared per-entity color legend above. */}
                    {visibleMetricSections.map((section) => (
                      <ReportableChart
                        key={section.id}
                        id={`section-${section.id}`}
                        title={section.title}
                        selected={reportIds.has(`section-${section.id}`)}
                        onToggle={toggleReportItem}
                      >
                      <div
                        className={barChartStyles["stacked-chart"]}
                      >
                        <div
                          className={styles["benchmark-scaling-head"]}
                          data-report-strip="true"
                        >
                          <div>
                            <h3 className={barChartStyles["stacked-chart__title"]}>
                              {section.title}
                            </h3>
                            <p className={styles["section-subtitle"]}>
                              {section.subtitle}
                            </p>
                          </div>
                        </div>
                        <div className={styles["chart-viz-tabpane"]}>
                          <GroupedMetricBars
                            rawData={normalizedRawData}
                            institutionIds={activeEntityIds}
                            stats={section.stats}
                            colorMap={benchmarkChartColorMap}
                          />
                        </div>
                      </div>
                      </ReportableChart>
                    ))}

                    {showAdvancedBenchmark ? (
                      <section className={styles["advanced-benchmark"]}>
                        <div className={styles["advanced-benchmark__grid"]}>
                          {showHeatmap ? (
                            <div className={styles["advanced-benchmark__split-cell"]}>
                              <ReportableChart
                                id="heatmap"
                                title="Benchmark heatmap"
                                selected={reportIds.has("heatmap")}
                                onToggle={toggleReportItem}
                              >
                                <LazyMountSection minHeight={380}>
                                  <BenchmarkHeatmap
                                    rawData={normalizedRawData}
                                    institutionIds={activeEntityIds}
                                    metricKey={advancedMetric.key}
                                    metricLabel={advancedMetric.label}
                                    metric={advancedMetric}
                                    metricOptions={visibleBarStats}
                                    onMetricChange={handleAdvancedMetricChange}
                                    entityLabel={capitalize(
                                      ENTITY_LABEL_BY_MODE[activeDataMode]
                                        ?.singular || "institution"
                                    )}
                                  />
                                </LazyMountSection>
                              </ReportableChart>
                            </div>
                          ) : null}
                          {showRelatedConcepts ? (
                            <div className={styles["advanced-benchmark__split-cell"]}>
                              <ReportableChart
                                id="related-concepts"
                                title="Related concepts"
                                selected={reportIds.has("related-concepts")}
                                onToggle={toggleReportItem}
                              >
                                <RelatedConceptsPanel
                                  items={relatedConceptItems}
                                  loading={relatedConceptLoading}
                                  error={relatedConceptError}
                                  onConceptClick={handleRelatedConceptClick}
                                />
                              </ReportableChart>
                            </div>
                          ) : null}
                        </div>
                      </section>
                    ) : null}

                    {showCitationsOverTime && citationByYearSeries.length > 0 ? (
                      <ReportableChart
                        id="citations-over-time"
                        title="Citations over time"
                        selected={reportIds.has("citations-over-time")}
                        onToggle={toggleReportItem}
                      >
                        <CitationByYearChart
                          series={citationByYearSeries}
                          colorMap={benchmarkChartColorMap}
                          entityLabel={
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
                            "entity"
                          }
                          title="Citations over time"
                          subtitle={`Annual citation counts for the selected ${
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
                            "entity"
                          }${citationByYearSeries.length > 1 ? "s" : ""}.`}
                        />
                      </ReportableChart>
                    ) : null}

                    {showPublicationsOverTime &&
                    publicationByYearSeries.length > 0 ? (
                      <ReportableChart
                        id="publications-over-time"
                        title="Publications over time"
                        selected={reportIds.has("publications-over-time")}
                        onToggle={toggleReportItem}
                      >
                        <CitationByYearChart
                          series={publicationByYearSeries}
                          colorMap={benchmarkChartColorMap}
                          entityLabel={
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
                            "entity"
                          }
                          title="Publications over time"
                          subtitle={`Annual publication counts for the selected ${
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
                            "entity"
                          }${publicationByYearSeries.length > 1 ? "s" : ""}.`}
                        />
                      </ReportableChart>
                    ) : null}

                    {showGrantsOverTime && grantsByYearSeries.length > 0 ? (
                      <ReportableChart
                        id="grants-over-time"
                        title="Grants over time"
                        selected={reportIds.has("grants-over-time")}
                        onToggle={toggleReportItem}
                      >
                        <CitationByYearChart
                          series={grantsByYearSeries}
                          colorMap={benchmarkChartColorMap}
                          entityLabel={
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
                            "entity"
                          }
                          title="Grants over time"
                          subtitle={`Annual grant counts for the selected ${
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
                            "entity"
                          }${grantsByYearSeries.length > 1 ? "s" : ""}.`}
                        />
                      </ReportableChart>
                    ) : null}

                    <div className={styles["bottom-section"]}>
                      <div className={styles["divider"]} />
                      <ReportableChart
                        id="breakdown"
                        title="Research Topics Specific Breakdown"
                        selected={reportIds.has("breakdown")}
                        onToggle={toggleReportItem}
                      >
                        <BreakdownTable
                          rawData={rawData}
                          institutionIds={activeEntityIds}
                          layout="entityRows"
                          entityIdKey={ENTITY_ID_KEY_BY_MODE[activeDataMode]}
                          entityLabel={capitalize(
                            ENTITY_LABEL_BY_MODE[activeDataMode]?.singular ||
                              "entity"
                          )}
                          subCols={breakdownCols}
                        />
                      </ReportableChart>
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
        </main>
        </div>
      </div>

      {/* ─── Report preview / export modal ─── */}
      <Modal
        open={previewOpen}
        onCancel={closeReportPreview}
        title="Preview report"
        width={820}
        okText={
          isExporting === "pdf" ? "Exporting…" : "Export PDF"
        }
        cancelText="Cancel"
        okButtonProps={{
          disabled: isCapturing || !captures.length || isExporting === "pdf",
        }}
        onOk={handleConfirmExport}
      >
        <div className={styles["report-preview"]}>
          {isCapturing ? (
            <div className={styles["report-preview__loading"]}>
              <Spin />
              <span>Rendering {reportItems.length} chart
                {reportItems.length > 1 ? "s" : ""}…</span>
            </div>
          ) : !captures.length ? (
            <div className={styles["report-preview__empty"]}>
              Nothing to preview. Add charts to the report, then try again.
            </div>
          ) : (
            <>
              <p className={styles["report-preview__hint"]}>
                {captures.length} item{captures.length > 1 ? "s" : ""}, in the
                order you added them. Export to download as a PDF, or cancel to
                keep editing your selection.
              </p>
              {captures.map((cap, idx) => (
                <div key={cap.id} className={styles["report-preview__item"]}>
                  <div className={styles["report-preview__item-title"]}>
                    {idx + 1}. {cap.title}
                  </div>
                  {cap.dataUrl ? (
                    <img
                      src={cap.dataUrl}
                      alt={cap.title}
                      className={styles["report-preview__img"]}
                    />
                  ) : null}
                  {cap.legend?.length ? (
                    <div className={styles["report-preview__legend"]}>
                      {cap.legend.map((l, i) => (
                        <span
                          key={`${l.name}-${i}`}
                          className={styles["report-preview__legend-item"]}
                        >
                          <span
                            className={styles["report-preview__legend-swatch"]}
                            style={{ backgroundColor: l.color }}
                          />
                          {l.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {cap.table?.rows?.length ? (
                    <div className={styles["report-preview__table-wrap"]}>
                      <table className={styles["report-preview__table"]}>
                        <thead>
                          <tr>
                            {cap.table.columns.map((c, i) => (
                              <th
                                key={c}
                                style={{
                                  textAlign: i === 0 ? "left" : "right",
                                }}
                              >
                                {c}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {cap.table.rows.map((row, ri) => (
                            <tr key={ri}>
                              {row.map((val, ci) => (
                                <td
                                  key={ci}
                                  style={{
                                    textAlign: ci === 0 ? "left" : "right",
                                  }}
                                >
                                  {val}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              ))}
            </>
          )}
        </div>
      </Modal>

      {/* ─── Save configuration version (label prompt) ─── */}
      <Modal
        open={saveModalOpen}
        onCancel={() => setSaveModalOpen(false)}
        title="Save configuration"
        okText={savingVersion ? "Saving…" : "Save version"}
        cancelText="Cancel"
        okButtonProps={{ disabled: savingVersion }}
        onOk={handleConfirmSave}
      >
        <p style={{ margin: "0 0 10px", color: "#64748b", fontSize: 13 }}>
          Saves your current customization as a new version
          {configVersion != null
            ? ` (the next version after v${configVersion})`
            : ""}
          . Add an optional label to find it later.
        </p>
        <Input
          autoFocus
          maxLength={255}
          placeholder="e.g. CS peer group – 5yr view"
          value={saveLabel}
          onChange={(e) => setSaveLabel(e.target.value)}
          onPressEnter={handleConfirmSave}
        />
      </Modal>

    </div>
  );
};

export default ResultsView;
